import React, { useMemo, useEffect } from 'react';
import { Row, Col, Typography, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import _ from 'lodash';

import { FormValue } from '../useForm';
import useProductCategory from 'src/queries/useProductCategory';
import { checkIsProductFinished } from 'src/utils/helper';
import { BLANK_OPTION, CATEGORY_TYPE } from 'src/constants/app';

type Props = {
  index: number;
  formInfo: UseFormReturn<FormValue, any>;
  dataReEstimate?: Response.QuotationItem;
  draftVariety: string;
  draftShape: string;
  setDraftVariety: (item: string) => void;
  setDraftShape: (item: string) => void;
  setDraftProduct: (item: string) => void;
};

const CategoriesList: React.FC<Props> = ({
  index,
  formInfo,
  draftVariety,
  dataReEstimate,
  setDraftVariety,
  draftShape,
  setDraftShape,
  setDraftProduct,
}) => {
  const { setValue } = formInfo;
  const { t } = useTranslation();
  const { categories } = useProductCategory();

  const categoriesData = useMemo(
    () =>
      categories &&
      categories.map((item) => ({
        label: item.categoryName || '',
        value: item.categoryId || '',
        childrenList: item.childrenList,
      })),
    [categories.length, JSON.stringify(categories)],
  );

  const subCategories = categoriesData
    ?.find((item) => item.value === draftVariety)
    ?.childrenList.map((item) => {
      return {
        label: item.categoryName,
        value: item.categoryId,
      };
    });

  const handleCategoryChange = (value: string, type: string) => {
    if (type === CATEGORY_TYPE.METERIAL) {
      setDraftVariety(value);
      setValue(
        `priceQuotes.${index}.isProductFinishedFlag`,
        checkIsProductFinished(value?.toString()),
      );

      const categoriesDataSelected =
        categoriesData?.find((item) => item.value === value)?.childrenList ??
        [];
      const isShapeValid = categoriesDataSelected.some(
        (item) => item.categoryId === draftShape,
      );
      if (!isShapeValid) {
        setDraftShape('');
      }
    }

    if (type === CATEGORY_TYPE.SHAPE) {
      setDraftShape(value);
    }

    setDraftProduct('');
  };

  useEffect(() => {
    if (!dataReEstimate?.hinshL3Cd && !dataReEstimate?.keijoL3Cd) {
      const defaultVariety = _.get(categoriesData, '[0].value');
      const defaultShape = _.get(subCategories, '[0].value');

      setDraftVariety(defaultVariety);
      setDraftShape(defaultShape);
    }
  }, [dataReEstimate]);

  return (
    <Row className="category-wrap">
      <Col span={12}>
        <Row className="category_block_infor_row variety_row">
          <Col xs={24} sm={24} md={5} lg={6} xl={6} className="category-label">
            <Typography.Text className="category_label text_category_label">
              {t('pricing_page.category_field.category_label')}
            </Typography.Text>
          </Col>
          <Col xs={20} sm={18} md={19} lg={12} xl={14}>
            <Select
              showSearch
              onChange={(value) =>
                handleCategoryChange(value, CATEGORY_TYPE.METERIAL)
              }
              options={[
                BLANK_OPTION,
                ...(categoriesData.filter(
                  (item) => item && item.value && item.value.trim() !== '',
                ) || []),
              ]}
              value={draftVariety}
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Col>
        </Row>
      </Col>
      <Col span={12}>
        <Row className="category_block_infor_row">
          <Col xs={24} sm={24} md={5} lg={4} xl={4} className="category-label">
            <Typography.Text className="category_label label_shape text_category_label">
              {checkIsProductFinished(draftVariety)
                ? t('pricing_page.category_field.type')
                : t('pricing_page.category_field.shape_field')}
            </Typography.Text>
          </Col>
          <Col xs={20} sm={18} md={19} lg={20} xl={20}>
            <Select
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onChange={(value) =>
                handleCategoryChange(value, CATEGORY_TYPE.SHAPE)
              }
              options={[BLANK_OPTION, ...(subCategories || [])]}
              value={draftShape}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default CategoriesList;
